import React, { useEffect, useState, useMemo } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";
import MapComponent from "../Home/MapComponent";

const MapPage = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("individual");
  const [groupedSpeakers, setGroupedSpeakers] = useState({});
  const [rawSpeakers, setRawSpeakers] = useState([]);

  useEffect(() => {
    AOS.init({ duration: 800 });
    fetchRegions();
  }, []);

  const regionStyles = {
    all: { bg: "#5365ED", icon: "fas fa-globe-asia" },
    asia: { bg: "#D35400", icon: "fas fa-pagelines" },
    africa: { bg: "#F39C12", icon: "fas fa-mountain" },
    europe: { bg: "#1ABC9C", icon: "fas fa-euro-sign" },
    americas: { bg: "#C0392B", icon: "fas fa-globe-americas" },
  };

  const fetchRegions = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/speakers/maps/regions");
      setRegions(response.data || []);
    } catch (error) {
      console.error("Error fetching regions:", error);
    }
  };

  const handleRegionClick = (regionKey) => {
    setSelectedRegion(regionKey);
    fetchSpeakersByRegion(regionKey);
  };

  const fetchSpeakersByRegion = async (regionKey) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/speakers/maps/countries?region=${regionKey}`
      );

      const countries = response.data || [];

      // دمج جميع المتحدثين من كل دولة في مصفوفة واحدة
      const allSpeakers = countries.flatMap(
        (country) => country.speakers || []
      );

      console.log("Merged Speakers:", allSpeakers);
      setRawSpeakers(allSpeakers);
    } catch (error) {
      console.error("Error fetching speakers:", error);
    }
  };

  const filteredSpeakers = speakers.filter((speaker) =>
    speaker.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupSpeakersByCountry = (speakers) => {
    const grouped = {};
    speakers.forEach((speaker) => {
      const countryName =
        speaker?.city?.state?.country?.arabicName || "غير معروف";
      if (!grouped[countryName]) {
        grouped[countryName] = [];
      }
      grouped[countryName].push(speaker);
    });
    return grouped;
  };

  const groupSpeakersByCity = (speakers) => {
    const grouped = {};
    speakers.forEach((speaker) => {
      const city = speaker?.city?.arabicName || "غير محددة";
      if (!grouped[city]) grouped[city] = [];
      grouped[city].push(speaker);
    });
    return grouped;
  };

  useEffect(() => {
    if (viewMode === "individual") {
      setGroupedSpeakers(groupSpeakersByCountry(rawSpeakers));
    } else if (viewMode === "city") {
      setGroupedSpeakers(groupSpeakersByCity(rawSpeakers));
    }
  }, [viewMode, rawSpeakers]);

  useEffect(() => {
    fetchRegions();
    fetchSpeakersByRegion("All");
  }, []);

  return (
    <main className="py-5" dir={languageDirection}>
      <style>
        {`
    #map-container {
      height: 600px;
      width: 100%;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    .region-card {
      border-radius: 10px;
      transition: all 0.3s ease;
      cursor: pointer;
      overflow: hidden;
      border: 2px solid transparent;
    }
    
    .region-card:hover {
      transform: translateY(-5px);
      border-color: var(--primary-color);
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    }
    
    .region-card:hover .card-body {
      background-color: rgba(107, 114, 224, 0.05);
    }
    
    .region-card:active {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    .region-card.active {
      border-color: var(--primary-color);
      background-color: rgba(var(--primary-rgb), 0.05);
    }
    
    .region-card img {
      height: 140px;
      object-fit: cover;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }
    
    .speaker-list-card {
      transition: all 0.3s ease;
      border: 1px solid #dee2e6;
    }
    
    .speaker-list-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
      border-color: #6B72E0;
    }
    
    .speaker-list-card:hover .btn-outline-primary {
      background-color: var(--primary-color);
      color: white;
    }
    
    .speaker-img {
      width: 60px;
      height: 60px;
      object-fit: cover;
      border-radius: 50%;
      border: 2px solid #6B72E0;
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
      background-color: #f8f9fa;
    }
    
    .leaflet-popup-content {
      min-width: 200px;
    }
    
    .leaflet-popup-content img {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      margin-right: 15px;
      object-fit: cover;
    }
    
    .marker-cluster {
      background-color: rgba(var(--primary-rgb), 0.6);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
    }
    
    .marker-speaker {
      background-color: var(--primary-color);
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }
    
    .search-container {
      position: relative;
      margin-bottom: 20px;
    }
    
    .search-container .form-control {
      padding-left: 40px;
      padding-right: 20px;
      border-radius: 20px;
    }
    
    .search-container .search-icon {
      position: absolute;
      left: 15px;
      top: 50%;
      transform: translateY(-50%);
      color: #aaa;
    }
    
    .custom-control-label {
      cursor: pointer;
    }
    
    .country-checkbox {
      margin-bottom: 8px;
    }
    
    .region-info h5 {
      margin-bottom: 5px;
    }
    
    .region-info p {
      margin-bottom: 15px;
      font-size: 0.9rem;
      color: var(--bs-gray-600);
    }
    
    /* Responsive adjustments */
    @media (max-width: 992px) {
      #map-container {
        height: 400px;
      }
      
      .region-cards-container {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        padding-bottom: 15px;
        margin-bottom: 20px;
      }
      
      .region-card {
        min-width: 200px;
        margin-right: 15px;
      }
      
      .region-card img {
        height: 100px;
      }
    }
    
    @media (max-width: 768px) {
      #map-container {
        height: 350px;
      }
      
      .speaker-list-container {
        max-height: 400px;
        overflow-y: auto;
      }
    }

    /* Direction-specific styles - will be toggled by JavaScript */
    html[dir="ltr"] .search-container .search-icon {
      left: auto;
      right: 15px;
    }
    
    html[dir="ltr"] .search-container .form-control {
      padding-left: 20px;
      padding-right: 40px;
    }
    
    html[dir="ltr"] .speaker-img {
      margin-right: 0;
      margin-left: 15px;
    }
    
    html[dir="ltr"] .region-card {
      margin-right: 0;
      margin-left: 15px;
    }
    
    /* Country select dropdown */
    .country-select-container {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      min-height: 40px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      overflow: hidden;
    }
    
    .country-search {
      flex: 1;
      min-width: 120px;
      box-shadow: none !important;
    }
    
    .country-dropdown {
      max-height: 200px;
      overflow-y: auto;
      z-index: 1000;
      padding: 0.5rem 0;
    }
    
    .country-dropdown .dropdown-item {
      padding: 0.5rem 1rem;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }
    
    .country-dropdown .dropdown-item:hover {
      background-color: rgba(107, 114, 224, 0.1);
    }
    
    /* Country badges */
    .country-badge {
      display: inline-flex;
      align-items: center;
      background-color: rgba(107, 114, 224, 0.1);
      color: #6B72E0;
      border-radius: 50px;
      padding: 0.25rem 0.75rem;
      margin: 0.25rem;
      font-size: 0.875rem;
      transition: all 0.2s ease;
    }
    
    .country-badge:hover {
      background-color: rgba(107, 114, 224, 0.2);
    }
    
    .remove-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      cursor: pointer;
      border-radius: 50%;
      transition: all 0.2s ease;
    }
    
    .remove-badge:hover {
      background-color: rgba(107, 114, 224, 0.3);
    }
    
    .selected-countries {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
    }`}
      </style>
      <div className="container">
        <div className="row mb-5">
          <div className="col-12 text-center">
            <h1 className="display-5 fw-bold text-primary-custom mb-3">
              {t("speakers_map")}
            </h1>
            <p className="lead mb-4">{t("speakers_map_subtitle")}</p>
          </div>
        </div>

        {/* Regions Cards Section - Improved Design */}
        <div className="card shadow-sm mb-5 border-0">
          <div className="card-header bg-white border-bottom border-primary-custom">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="h5 mb-0 text-primary-custom fw-bold">
                {t("regions")}
              </h3>
              {/* <button className="btn btn-sm btn-outline-primary">
                {t("view_all")}
              </button> */}
            </div>
          </div>
          <div className="card-body py-4">
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-5 g-4">
              {regions.map((region, index) => {
                const { bg, icon } = regionStyles[region.regionKey] || {
                  bg: "#cccccc",
                  icon: "fas fa-globe",
                };

                return (
                  <div
                    className="col"
                    key={region.regionKey}
                    data-aos="fade-up"
                    data-aos-delay={index * 100} // تأخير بسيط بين الكروت
                  >
                    <div
                      className="card h-100 region-card overflow-hidden shadow-sm"
                      data-region={region.regionKey.toLowerCase()}
                    >
                      <div className="position-relative">
                        <div
                          style={{
                            backgroundColor: bg,
                            height: "120px",
                            width: "100%",
                            backgroundImage:
                              "radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.15) 0%, transparent 60%)",
                          }}
                          className="d-flex align-items-center justify-content-center"
                        >
                          <i
                            className={`${icon} fa-3x text-white opacity-50 position-absolute end-0 bottom-0 me-3 mb-2`}
                          ></i>
                          <h4 className="text-white fw-bold mb-0 py-4">
                            {region.regionName}
                          </h4>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div
                              className="bg-primary-custom rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: 36, height: 36 }}
                            >
                              <i className="fas fa-user-tie text-white"></i>
                            </div>
                            <span className="fw-medium ms-2">
                              {region.speakerCount}
                            </span>
                          </div>
                          <span className="badge bg-primary-custom rounded-pill px-3 py-2">
                            {t("speakers")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Map and Speakers Section */}
        <div className="row">
          <div className="col-lg-8 mb-4">
            <MapComponent
              regions={regions}
              onRegionClick={handleRegionClick}
              viewMode={viewMode}
              speakers={rawSpeakers}
            />
          </div>
          <div className="col-lg-4">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="m-0 fw-bold text-primary-custom">
                      {selectedRegion || t("all_regions")}
                    </h5>
                    <small className="text-muted">{t("speakers")}</small>
                  </div>
                  <span className="badge bg-primary-custom rounded-pill">
                    {filteredSpeakers.speakerCount}
                  </span>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="p-3 border-bottom">
                  {/* حقل البحث */}
                  <div className="search-container position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("search_speaker")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <i
                      className="fas fa-search search-icon position-absolute"
                      style={{
                        top: "50%",
                        right: "10px",
                        transform: "translateY(-50%)",
                      }}
                    ></i>
                  </div>

                  {/* أزرار طريقة العرض */}
                  <div className="mt-3 mb-2 d-flex align-items-center justify-content-between">
                    <span className="fw-medium">{t("view_mode")}</span>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className={`btn btn-outline-primary ${
                          viewMode === "individual" ? "active" : ""
                        }`}
                        onClick={() => setViewMode("individual")}
                      >
                        {t("individual")}
                      </button>
                      <button
                        type="button"
                        className={`btn btn-outline-primary ${
                          viewMode === "city" ? "active" : ""
                        }`}
                        onClick={() => setViewMode("city")}
                      >
                        {t("cities")}
                      </button>
                    </div>
                  </div>
                </div>

                {/* قائمة المتحدثين الأفراد */}
                {viewMode === "individual" && (
                  <div className="p-3 speaker-list-container">
                    {Object.keys(groupedSpeakers).length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-users fa-3x mb-3 opacity-25"></i>
                        <p>{t("no_speakers_in_region")}</p>
                      </div>
                    ) : (
                      Object.entries(groupedSpeakers).map(
                        ([countryName, speakersList]) => {
                          const filtered = speakersList.filter((s) =>
                            (s.arabicFullName || s.englishFullName || "")
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase())
                          );

                          if (filtered.length === 0) return null;

                          return (
                            <div key={countryName}>
                              <div className="mb-3 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0">
                                  {countryName || "بدون دولة"}
                                </h6>
                                <span className="badge bg-primary-custom rounded-pill">
                                  {filtered.length}
                                </span>
                              </div>

                              {filtered.map((speaker, idx) => (
                                <div
                                  key={idx}
                                  className="card mb-3 border speaker-list-card"
                                >
                                  <div className="card-body p-3">
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="me-3 d-flex align-items-center justify-content-center"
                                        style={{
                                          minWidth: 60,
                                          width: 60,
                                          height: 60,
                                        }}
                                      >
                                        <img
                                          src={speaker.profileImage}
                                          alt={speaker.arabicFullName}
                                          onError={(e) =>
                                            (e.target.src =
                                              "/website/assets/default-avatar.png")
                                          }
                                          className="speaker-img"
                                          style={{
                                            border: "2px solid #6B72E0",
                                            backgroundColor: "#fff",
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <h6 className="mb-0">
                                          {speaker.arabicFullName}
                                        </h6>
                                        <small className="text-muted d-block">
                                          {speaker.arabicJobTitle}
                                        </small>
                                        <small className="text-muted">
                                          <i className="fas fa-map-marker-alt text-danger me-1"></i>
                                          {
                                            speaker?.city?.state?.country
                                              ?.arabicName
                                          }
                                        </small>
                                      </div>
                                    </div>
                                    <div className="mt-2 d-flex justify-content-end">
                                      <a
                                        href={`/speaker-detail/${speaker.id}`}
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        {t("view_profile")}{" "}
                                        <i className="fas fa-arrow-left ms-1"></i>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        }
                      )
                    )}
                  </div>
                )}
                {/* قائمة المتحدثين حسب الدولة */}
                {viewMode === "city" && (
                  <div className="p-3 speaker-list-container">
                    {Object.entries(groupSpeakersByCity(rawSpeakers)).map(
                      ([cityName, speakersList], index) => {
                        if (speakersList.length === 0) return null;

                        return (
                          <div
                            key={index}
                            className="card mb-3 border speaker-list-card"
                          >
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <h6 className="mb-0">{cityName}</h6>
                                  <small className="text-muted">
                                    <i className="fas fa-map-marker-alt text-danger me-1"></i>
                                    {speakersList[0]?.city?.state?.country
                                      ?.arabicName || "غير معروف"}
                                  </small>
                                </div>
                                <span className="badge bg-primary-custom rounded-pill">
                                  <i className="fas fa-user-tie me-1"></i>{" "}
                                  {speakersList.length}
                                </span>
                              </div>

                              <div className="mt-3 city-speakers-preview">
                                <div className="d-flex">
                                  {speakersList
                                    .slice(0, 4)
                                    .map((speaker, idx) => (
                                      <div className="me-1" key={idx}>
                                        <img
                                          src={speaker.profileImage}
                                          alt={speaker.arabicFullName}
                                          style={{
                                            width: "35px",
                                            height: "35px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                          }}
                                          onError={(e) =>
                                            (e.target.src =
                                              "/website/assets/default-avatar.png")
                                          }
                                        />
                                      </div>
                                    ))}
                                  {speakersList.length > 4 && (
                                    <div
                                      className="me-1 d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "35px",
                                        height: "35px",
                                        borderRadius: "50%",
                                        backgroundColor: "#f0f0f0",
                                        color: "#666",
                                      }}
                                    >
                                      +{speakersList.length - 4}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* <div className="mt-2 d-flex justify-content-end">
                                <button
                                  className="btn btn-sm btn-outline-primary city-details-btn"
                                  onClick={() => {
                                    // ممكن تضيف منطق لعرض صفحة جديدة أو توسيع البطاقة
                                    alert(`عرض المتحدثين في ${cityName}`);
                                  }}
                                >
                                  <span>{t("view_speakers")}</span>{" "}
                                  <i className="fas fa-arrow-left ms-1"></i>
                                </button>
                              </div> */}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default MapPage;
